import React, { useCallback, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { Box, Grid, Paper, Typography, Theme } from '@mui/material'
import { makeStyles } from '@mui/styles'

import { CalendarDateParams, DateObject } from './ProfessocialCalendar'
import CalendarWeek from './modules/CalendarWeek'
import useDisplayCalendar from './utils/displayCalendarHook'
import { getCalendarDataInitiated, updatePostTemplateInitiated } from '../../../../store/dashboard/dashboard.actions'
import { useDispatch, useSelector } from 'react-redux'
import { UpdatePostTemplateRequest } from '@3m5/sm_publish-frontend-api'
import { PostContentUpdateElement, PostTemplateUpdateElement } from '@3m5/sm_publish-frontend-api'
import { datesAreOnSameDay } from './utils/datesAreTheSame'
import { setTimeZone } from '@professocial/professocialFormComponents/FormComponents/util/formatDate'
import { ActionCall } from '@professocial/professocialTypes/types/commonTypes'
import { CoreRootState } from '../../../../store/coreRoot.reducer'
import { namedRequestData } from '../../../../store/common/common.selectors'
import Preloader from '@professocial/professocialFormComponents/FormComponents/Preloader'

export interface CalendarDayObject {
	month: number;
	day: number;
	year: number;
	date: Date;
	weekday: number;
	actualDay: boolean;
	actualMonth: boolean;
	published: boolean;
}

export interface WeekObject {
	days: CalendarDayObject[];
}

export interface DraggedItemData {
	postStart?: Date;
	day: string;
	id: string;
}

export interface dndStateProps {
	draggedTo?: number;
	isDragging: boolean;
	isDropped: boolean;
	draggedItemData?: DraggedItemData;
	newStartDate?: number;
}

const initialDnDState: dndStateProps = {
  isDragging: false,
  isDropped: false,
}

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    marginTop: theme.spacing(0.8),
    padding: 0,
    overflow: 'hidden',
  },
  calendarDays: {},
  calendarDaysHeadline: {
    display: 'flex',
    justifyContent: 'space-between',
    flexWrap: 'nowrap',
    width: '100%',
    backgroundColor: theme.palette.primary.main,
  },
  calendarDaysWeekday: {
    width: '15%',
    maxWidth: '15%',
    textAlign: 'center',
    verticalAlign: 'middle',
    height: '40px',
    textTransform: 'uppercase',
    borderRight: '1px solid #f5f5f5',
    padding: '5px',
    color: theme.palette.primary.contrastText,
    lineHeight: '33px',
    '&:last-child': {
      borderRight: 'none',
    },
  },
  backgroundLayer: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    background: 'rgb(255,255,255,0.6)',
  },
  preloader: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%,-50%)',
  },
}))

interface IProps {
	loading: boolean;
	setLoading?: (value: boolean) => void;
	selectedDate?: number;
	calendarData?: DateObject;
	changeCalendarData?: (data: DateObject) => void;
	changeSelectedDate?: (date: number) => void;
	changeDataParams?: (params: CalendarDateParams) => void;
}

const PostCalendar: React.FC<IProps> = (props) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const DaysOfWeek = [
    t('coreCalendar:monday'),
    t('coreCalendar:tuesday'),
    t('coreCalendar:wednesday'),
    t('coreCalendar:thursday'),
    t('coreCalendar:friday'),
    t('coreCalendar:saturday'),
    t('coreCalendar:sunday'),
  ]

  const [selectedDate, setSelectedDate] = React.useState<number>(props.selectedDate ? props.selectedDate : Date.now())
  const [completeCalendarWeeks, setCompleteCalendarWeeks] = useDisplayCalendar(props.changeDataParams)
  const [dragAndDrop, setDragAndDrop] = React.useState(initialDnDState)

  const dispatchUpdatePostTemplate = useCallback((props: UpdatePostTemplateRequest) => dispatch(updatePostTemplateInitiated(props)), [dispatch])
  const loadingCalendarData: ActionCall | undefined = useSelector((state: CoreRootState) => namedRequestData(state, getCalendarDataInitiated.type))

  // const editSettings = (topicId: string | undefined, aboType: AboType) => {
  //
  // }

  useEffect(() => {
    props.selectedDate && setSelectedDate(props.selectedDate)
  }, [props.selectedDate],
  )

  useEffect(() => {
    props.setLoading && props.setLoading(false)
  }, [props.calendarData],
  )

  useEffect(() => {
    setCompleteCalendarWeeks(selectedDate)
  }, [selectedDate],
  )

  useEffect(() => {
    if (loadingCalendarData && loadingCalendarData.status === 'success') {
      setDragAndDrop({
        ...dragAndDrop,
        isDragging: false,
        draggedItemData: undefined,
        newStartDate: undefined,
        draggedTo: undefined,
      })
    }
  }, [loadingCalendarData])

  const handleDnD = (data: dndStateProps) => {
    if (data && props.calendarData && data.draggedItemData) {
      const postData = props.calendarData[data.draggedItemData.day][data.draggedItemData.id]

      const postTemplateUpdateElement: PostTemplateUpdateElement = {
        postContent: postData?.postContent as PostContentUpdateElement,
        active: postData?.template?.active || false,
        postEnd: postData?.template?.postEnd,
        postStart: (data.newStartDate && new Date(setTimeZone(data.newStartDate))) || (postData?.template?.postStart && new Date(setTimeZone(postData.template.postStart.getTime()))),
        linkIndividualizationAllowed: postData?.template?.linkIndividualizationAllowed || false,
        hashtagIndividualizationAllowed: postData?.template?.hashtagIndividualizationAllowed || false,
        topicId: postData?.template?.topicId,
        tags: postData?.template?.tags || [],
        allowedNetworks: postData?.template?.allowedNetworks || [],
      }

      const daysAreEqual = data.newStartDate && postData?.template?.postStart && datesAreOnSameDay(new Date(data.newStartDate), postData.template.postStart)

      switch (true) {
      case !data.isDragging && !daysAreEqual: {
        dispatchUpdatePostTemplate({ templateId: data.draggedItemData.id, postTemplateUpdateElement })

        const obj = { ...props.calendarData }
        delete obj[data.draggedItemData.day][data.draggedItemData.id]
        props.changeCalendarData && props.changeCalendarData(obj)

        setDragAndDrop({
          ...dragAndDrop,
          isDragging: false,
        })
        break
      }
      case !data.isDragging && daysAreEqual:
        setDragAndDrop({
          ...dragAndDrop,
          isDragging: false,
          draggedItemData: undefined,
          newStartDate: undefined,
          draggedTo: undefined,
        })
        break
      case data.isDragging:
        setDragAndDrop(data)
        break
      }
    }
  }

  const handleSetSelectedDate = (selectedDate: number) => {
    setSelectedDate(selectedDate)
  }

  return (
    <Grid
      container
      direction="row"
    >
      <Grid container item xs={12}>
        <Paper className={classes.paper}>
          <Grid item xs={12}>
            <Box className={classes.calendarDays} component="div">
              <Box className={classes.calendarDaysHeadline} component="div">
                {DaysOfWeek.map((day, index) => {
                  return (
                    <Box key={index} className={classes.calendarDaysWeekday} component="div">
                      <Typography component="span" variant="body2">{day}</Typography>
                    </Box>
                  )
                })}
              </Box>
              <Box component="div">
                {completeCalendarWeeks && completeCalendarWeeks.map((week: WeekObject, index: number) => {
                  return (
                    <CalendarWeek
                      key={index}
                      index={index}
                      week={week}
                      // editSettings={editSettings}
                      setSelectedDate={handleSetSelectedDate}
                      calendarData={props.calendarData}
                      changeCalendarData={props.changeCalendarData}
                      dragAndDrop={dragAndDrop}
                      handleDnD={handleDnD}
                    />
                  )
                })}
                {!props.calendarData && (
                  <>
                    <div className={classes.backgroundLayer}/>
                    <div className={classes.preloader}><Preloader color="secondary"/></div>
                  </>
                )}
              </Box>
            </Box>
          </Grid>
        </Paper>
      </Grid>
    </Grid>
  )
}
export default PostCalendar
